<template>
  <div>
    <div class="card">
      <div class="card-header py-2">
        <Spinner v-if="loading" margin_right="10px" height="20px" />
        <b-form-checkbox v-else class="display-inline marginright5" v-model="checkedAll" :disabled="!loaded"></b-form-checkbox>
        <span @click="loaded ? (checkedAll = !checkedAll) : null">Komoot Trail View</span>
        <div style="float: right;">
          <img @click="expanded = !expanded" style="height: 13px; cursor: pointer;" :src="expanded ? icons.chevronUp : icons.chevronDown" />
        </div>
      </div>
      <div class="card-body" v-if="expanded">
        <b-form-checkbox class="display-inline marginright5" v-model="active"></b-form-checkbox>
        <span @click="active = !active">Active</span>
        <b-form-checkbox class="display-inline marginright5" v-model="inactive"></b-form-checkbox>
        <span @click="inactive = !inactive">Inactive</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
:global(.komoottrailview-pointer-cursor),
:global(.komoottrailview-pointer-cursor *) {
  cursor: pointer !important;
}
</style>

<script lang="ts">
import BaseComponent from './Base.vue';
import { EventBus } from '@/ts/EventBus';
import Photo from '@/ts/Photo';
import { AlertStatus, DownloadStatus } from '@/ts/types';
import { defineComponent } from "vue";
import L from 'leaflet';
import { DataFilterExtension } from '@deck.gl/extensions';
import { DeckOverlay } from '@deck.gl-community/leaflet';
import { MapLasScatterplotLayer } from '@/ts/MapLasScatterplotLayer';
import lightGallery from 'lightgallery';
import lgZoom from 'lightgallery/plugins/zoom';
import lgAutoplay from 'lightgallery/plugins/autoplay';

interface KomootTrailViewResponsePlace {
  id: number;
  lat: number;
  lon: number;
  is_active: boolean;
}

interface KomootTrailViewBinaryData {
  length: number;
  attributes: {
    getPosition: { value: Float32Array; size: number };
    getFillColor: { value: Uint8Array; size: 4 };
    getLineColor: { value: Uint8Array; size: 4 };
    getFilterValue: { value: Float32Array; size: 1 };
  };
}

interface KomootTrailViewPreparedData {
  data: KomootTrailViewBinaryData;
  results: KomootTrailViewResponsePlace[];
}

interface KomootTrailViewCardInstanceState {
  komootTrailViewResult: KomootTrailViewResponsePlace[] | null;
  komootTrailViewLayerResults: KomootTrailViewResponsePlace[];
  komootTrailViewLayer: MapLasScatterplotLayer | null;
  deckOverlay: DeckOverlay | null;
  mapMouseMoveHandler: ((event: L.LeafletMouseEvent) => void) | null;
  mapMouseOutHandler: (() => void) | null;
  mapClickHandler: ((event: L.LeafletMouseEvent) => void) | null;
}

declare module 'vue' {
  interface ComponentCustomProperties extends KomootTrailViewCardInstanceState {}
}

export default defineComponent({
  extends: BaseComponent,
  data() {
    return {
      checkedAll: false,
      expanded: false,
      active: true,
      inactive: false,
      loaded: false,
      loading: false,
    };
  },
  created() {
    this.komootTrailViewResult = null;
    this.komootTrailViewLayerResults = [];
    this.komootTrailViewLayer = null;
    this.deckOverlay = null;
    this.mapMouseMoveHandler = null;
    this.mapMouseOutHandler = null;
    this.mapClickHandler = null;
    EventBus.$on('downloadExternalData', () => this.downloadPlacesKomootTrailView());
    EventBus.$on('toggleKomootTrailView', () => {
      if (this.komootTrailViewResult) {
        this.checkedAll = !this.checkedAll;
      }
    });
    EventBus.$on('selectedAreaChanged', () => this.onSelectedAreaChanged());
  },
  beforeUnmount() {
    if (window.mainmap && this.mapMouseMoveHandler) {
      window.mainmap.off('mousemove', this.mapMouseMoveHandler);
      this.mapMouseMoveHandler = null;
    }
    if (window.mainmap && this.mapMouseOutHandler) {
      window.mainmap.off('mouseout', this.mapMouseOutHandler);
      this.mapMouseOutHandler = null;
    }
    if (window.mainmap && this.mapClickHandler) {
      window.mainmap.off('click', this.mapClickHandler);
      this.mapClickHandler = null;
    }
    if (window.mainmap) {
      window.mainmap.getContainer().classList.remove('komoottrailview-pointer-cursor');
    }
    if (this.deckOverlay && window.mainmap) {
      window.mainmap.removeLayer(this.deckOverlay);
      this.deckOverlay = null;
    }
  },
  methods: {
    isPointInSelectedArea(lat: number, lon: number) {
      // @ts-ignore
      const areaKey = window.selectedArea;
      if (!areaKey) return true;
      // @ts-ignore
      const area = window.areas[areaKey];
      if (!area || !area[0] || !area[1]) return true;
      const minLat = Math.min(area[0][0], area[1][0]);
      const maxLat = Math.max(area[0][0], area[1][0]);
      const minLon = Math.min(area[0][1], area[1][1]);
      const maxLon = Math.max(area[0][1], area[1][1]);
      if (Number.isNaN(lat) || Number.isNaN(lon)) return false;
      return lat >= minLat && lat <= maxLat && lon >= minLon && lon <= maxLon;
    },
    createBinaryData(results: KomootTrailViewResponsePlace[]): KomootTrailViewPreparedData {
      const visibleResults = results.filter((place) => this.isPointInSelectedArea(place.lat, place.lon));
      const positions = new Float32Array(visibleResults.length * 2);
      const fillColors = new Uint8Array(visibleResults.length * 4);
      const lineColors = new Uint8Array(visibleResults.length * 4);
      const filterValues = new Float32Array(visibleResults.length);

      visibleResults.forEach((place, index) => {
        positions[index * 2] = place.lon;
        positions[index * 2 + 1] = place.lat;
        fillColors.set([0, 0, 255, 51], index * 4);
        lineColors.set([0, 0, 255, 255], index * 4);
        filterValues[index] = place.is_active ? 1 : 0;
      });

      return {
        results: visibleResults,
        data: {
          length: visibleResults.length,
          attributes: {
            getPosition: { value: positions, size: 2 },
            getFillColor: { value: fillColors, size: 4 },
            getLineColor: { value: lineColors, size: 4 },
            getFilterValue: { value: filterValues, size: 1 },
          },
        },
      };
    },
    ensureDeckOverlay() {
      if (!window.mainmap) return;
      if (!this.deckOverlay) {
        this.deckOverlay = new DeckOverlay({ layers: [] });
        window.mainmap.addLayer(this.deckOverlay);
      }
      if (!this.mapMouseMoveHandler) {
        this.mapMouseMoveHandler = (event: L.LeafletMouseEvent) => this.onMapMouseMove(event);
        window.mainmap.on('mousemove', this.mapMouseMoveHandler);
      }
      if (!this.mapMouseOutHandler) {
        this.mapMouseOutHandler = () => window.mainmap.getContainer().classList.remove('komoottrailview-pointer-cursor');
        window.mainmap.on('mouseout', this.mapMouseOutHandler);
      }
      if (!this.mapClickHandler) {
        this.mapClickHandler = (event: L.LeafletMouseEvent) => this.onMapClick(event);
        window.mainmap.on('click', this.mapClickHandler);
      }
    },
    onMapMouseMove(event: L.LeafletMouseEvent) {
      const { lat, lng } = event.latlng;
      const index = this.checkedAll && this.komootTrailViewLayer ? this.komootTrailViewLayer.pick(lat, lng) : null;
      window.mainmap.getContainer().classList.toggle('komoottrailview-pointer-cursor', index !== null);
    },
    onMapClick(event: L.LeafletMouseEvent) {
      const { lat, lng } = event.latlng;
      if (!this.checkedAll || !this.komootTrailViewLayer) return;
      const index = this.komootTrailViewLayer.pick(lat, lng);
      if (index === null) return;
      const place = this.komootTrailViewLayerResults[index];
      if (!place) return;

      const coords = `${place.lat},${place.lon}`;
      const popupContent = document.createElement('div');
      const title = document.createElement('b');
      title.textContent = String(place.id);
      const mapLink = document.createElement('a');
      mapLink.href = `https://maps.google.com/maps?q=${coords}`;
      mapLink.target = '_blank';
      mapLink.rel = 'noopener noreferrer';
      mapLink.textContent = coords;
      const image = document.createElement('img');
      image.alt = String(place.id);
      image.style.cssText = 'display: block; max-width: 100%; max-height: 180px; margin-top: 6px; cursor: pointer;';
      const photo = this.createKomootTrailViewPhoto(place.id);
      image.src = photo.image_thumb;
      void photo.generate_image_thumb_cache().then(() => {
        image.src = photo.image_thumb_cache;
      });
      image.addEventListener('click', () => this.openPhotoGallery(photo));
      popupContent.append(title, document.createTextNode(' '), mapLink, image);
      window.mainmap.openPopup(popupContent, [place.lat, place.lon]);
    },
    createKomootTrailViewPhoto(placeId: number): Photo {
      const fullImage = `${window.mediaHost}komoot_trail_view/${placeId}.jpg`;
      const thumbnail = `${window.mediaHost}komoot_trail_view/${placeId}_thumb.jpg`;
      return new Photo(placeId, 'Photo', '', String(placeId), null, fullImage, fullImage, thumbnail, false, 1, false);
    },
    async openPhotoGallery(photo: Photo) {
      await Promise.all([photo.generate_image_fullhd_cache(), photo.generate_image_thumb_cache()]);
      const gallery = lightGallery(document.getElementById('map'), {
        dynamic: true,
        dynamicEl: [{ src: photo.image_fullhd_cache, thumb: photo.image_thumb_cache }],
        plugins: [lgZoom, lgAutoplay],
        showZoomInOutIcons: true,
        slideShowInterval: 3000,
        allowMediaOverlap: true,
        mobileSettings: {
          controls: false,
          showCloseIcon: true,
          download: false,
        },
      });
      gallery.openGallery();
    },
    updateKomootTrailViewLayer() {
      this.ensureDeckOverlay();
      if (!this.deckOverlay) return;
      if (this.komootTrailViewLayer) {
        this.komootTrailViewLayer.setVisible(this.checkedAll);
      }
      this.deckOverlay.setProps({ layers: this.komootTrailViewLayer ? [this.komootTrailViewLayer.getLayer()] : [] });
    },
    getStatusFilterRange(): readonly [number, number] {
      if (this.active && this.inactive) return [0, 1];
      if (this.active) return [1, 1];
      if (this.inactive) return [0, 0];
      return [2, 2];
    },
    onStatusFilterChanged() {
      if (!this.komootTrailViewLayer) return;
      this.komootTrailViewLayer.setFilterRange(this.getStatusFilterRange());
      this.updateKomootTrailViewLayer();
    },
    createKomootTrailViewLayer() {
      if (!this.komootTrailViewResult) return;
      const preparedData = this.createBinaryData(this.komootTrailViewResult);
      this.komootTrailViewLayerResults = preparedData.results;
      this.komootTrailViewLayer = new MapLasScatterplotLayer({
        id: 'komoot-trail-view-points',
        data: preparedData.data,
        extensions: [new DataFilterExtension({ filterSize: 1 })],
        filterRange: this.getStatusFilterRange(),
        positionFormat: 'XY',
        _normalize: false,
        getRadius: 8,
        radiusUnits: 'pixels',
        stroked: true,
        getLineWidth: 2,
        lineWidthUnits: 'pixels',
        visible: this.checkedAll,
        pickable: false,
      });
    },
    onKomootTrailViewCheckboxChanged() {
      if (this.checkedAll && !this.komootTrailViewLayer) {
        this.createKomootTrailViewLayer();
      }
      if (!this.checkedAll && window.mainmap) {
        window.mainmap.getContainer().classList.remove('komoottrailview-pointer-cursor');
      }
      this.updateKomootTrailViewLayer();
    },
    onSelectedAreaChanged() {
      if (this.checkedAll && this.komootTrailViewResult) {
        this.createKomootTrailViewLayer();
      } else {
        this.komootTrailViewLayer = null;
        this.komootTrailViewLayerResults = [];
      }
      if (this.checkedAll) {
        this.updateKomootTrailViewLayer();
      }
    },
    downloadPlacesKomootTrailView() {
      this.loading = true;
      const endPoint = window.apiHost + 'api/stringfield/komoottrailview/';
      this.downloadUrlWithCache(endPoint, 'komoottrailview', 'komoottrailview.txt', null, true).then((response) => {
        this.komootTrailViewResult = response[1].results;
        this.loaded = true;
        if (response[0] == DownloadStatus.cache_fresh) {
          this.createAlert(AlertStatus.success, this.$t('placesLoaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.api_fresh) {
          this.createAlert(AlertStatus.success, this.$t('placesDownloaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.cache_last) {
          this.createAlert(AlertStatus.warning, this.$t('placesLoaded', [response[1].results.length]).toString(), 2000);
        }
        if (this.checkedAll) {
          this.createKomootTrailViewLayer();
          this.updateKomootTrailViewLayer();
        }
      }).catch(() => {
        this.createAlert(AlertStatus.danger, this.$t('placesError').toString(), 2000);
      }).finally(() => {
        this.loading = false;
      });
    },
  },
  watch: {
    checkedAll() {
      this.onKomootTrailViewCheckboxChanged();
    },
    active() {
      this.onStatusFilterChanged();
    },
    inactive() {
      this.onStatusFilterChanged();
    },
  }
})


</script>